import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { AUDIENCES } from "../../data/marketing";

export default function AudienceEngine() {
  const [active, setActive] = useState(AUDIENCES[0].id);
  const audience = AUDIENCES.find((a) => a.id === active);
  return (
    <section id="audiences" className="bg-[#F4EFE6] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">02 · Who we help</div>
          <h2 className="mt-3 font-display text-3xl font-medium tracking-tight text-forest-deep sm:text-4xl">Your context. A mandate built around it.</h2>
          <p className="mt-4 text-base leading-relaxed text-forest-deep/65 sm:text-lg">A producer, a municipality and a hotel face different responsibilities. Select your organisation to see where we can help.</p>
        </div>
        <div className="mt-10 grid gap-8 lg:grid-cols-[0.9fr_1.3fr]">
          <div className="space-y-3">
            {AUDIENCES.map((item, index) => (
              <button key={item.id} type="button" onClick={() => setActive(item.id)} className={`relative w-full overflow-hidden rounded-2xl border p-5 text-left transition-all ${active === item.id ? "border-forest-primary bg-white shadow-lg shadow-forest-deep/10" : "border-forest-deep/10 bg-white/50 hover:bg-white"}`} data-testid={`audience-tab-${item.id}`}>
                {active === item.id && <motion.span layoutId="audience-active" className="absolute inset-y-0 left-0 w-1.5 bg-forest-primary" />}
                <span className="font-mono text-xs font-semibold text-forest-deep/40">0{index + 1}</span>
                <span className="mt-2 block font-display text-xl font-medium text-forest-deep">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="relative overflow-hidden rounded-3xl bg-forest-deep p-8 text-[#FBF9F4] sm:p-10" data-testid="audience-panel">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-emerald-400/10 blur-[80px]" />
            <AnimatePresence mode="wait">
              <motion.div key={audience.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.28 }} className="relative">
                <h3 className="font-display text-3xl font-medium">{audience.heading}</h3>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-emerald-100/70">{audience.body}</p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {audience.bullets.map((bullet, index) => (
                    <motion.div key={bullet} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="flex items-center gap-3 rounded-2xl bg-white/5 px-4 py-3 text-sm text-emerald-100/85">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" /> {bullet}
                    </motion.div>
                  ))}
                </div>
                <Link to={audience.link} className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-5 py-3 font-display text-sm font-bold text-forest-deep transition-all hover:-translate-y-0.5 hover:bg-emerald-300" data-testid="audience-link">
                  {audience.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
